import { useQuery } from '@apollo/client/react';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Link, TextField, Typography, useTheme
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { makeStyles } from 'tss-react/mui';
import Entries from 'containers/forms/Entries';
import SuggestActorForm from 'containers/forms/SuggestActorForm';
import gql from 'graphql-tag';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Switch from '@mui/material/Switch';
import DateFilter from '../../containers/layouts/agendaPage/DateFilter';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Slider from '@mui/material/Slider';
import { useSessionState } from '../../context/session/session';
import ParentContainer from './ParentContainer';
import SaveFilterButton from './SaveFilterButton';
import SavedFiltersList from './SavedFiltersList';
import NotificationSuggestion from '../NotificationSuggestion';
import useNotificationSuggestion from '../../hooks/useNotificationSuggestion';
import FilterTutorial from '../tutorial/FilterTutorial';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const useStyles = makeStyles()((theme) => ({
  root: {
    flexGrow: 1,
    height: '100%',
    maxWidth: 400,
    overflowY: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('md')]: {
      maxWidth: 'none',
    },
    '& > *:not(button)': {
      width: '100%',
      backgroundColor: 'white',
    },
  },
  collectionLabel: {
    textAlign: 'center',
    color: '#2C367E',
  },
  postCode: {
    '& input': {
      boxShadow: 'initial!important',
    },
    width: '100%',
    '& .MuiInputBase-root': {
      borderRadius: 0,
    },
  },
  listItem: {
    paddingTop: '0px',
    paddingBottom: '0px',
  },
  postCodeLayout: {
    textAlign: 'center',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  listEntries: {
    width: '100%',
    padding: 'inherit!important',
  },
  entriesExpend: {
    padding: 'inherit!important',
  },
  entries: {
    padding: '0px 5px 0px 5px!important',
  },
  expansionPanel: {
    margin: 'inherit!important',
    [theme.breakpoints.down('md')]: {
      '&:nth-last-child(2)': {
        boxShadow: 'none',
      },
    },
  },
  inviteActor: {
    width: '90%',
    margin: '0.5em auto',
    fontSize: '0.78rem',
    padding: '4px 10px',
    textTransform: 'none',
    boxShadow: 'none',
    fontWeight: 500,
  },
  expansionPanelSummary: {
    padding: '0px 5px 0px 5px!important',
    minHeight: '40px !important',
    '& .MuiAccordionSummary-content': {
      margin: '8px 0 !important',
      alignItems: 'center',
    },
  },
  expansionPanelDetails: {
    flexDirection: 'column',
  },
  entriesCheckbox: {
    padding: '1px',
  },
  filterButton: {
    color: 'white',
    backgroundColor: '#2c367e !important',
    position: 'sticky',
    bottom: 0,
    margin: '10px 0',
    width: '80%',
    flexShrink: 0,
    zIndex: 1,
  },
  filterButtonIcon: {
    transform: 'rotate(180deg)',
  },
  listItemText: {
    marginTop: '0px',
    marginBottom: '0px',
  },
  paper: {
    position: 'absolute',
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
    width: '60%',
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(0.5),
    top: theme.spacing(0.5),
    color: theme.palette.grey[500],
  },
  favoriteGrid: {
    textAlign: 'center',
    borderBottom: '0.1em solid rgb(200, 200, 200)',
    borderWidth: '1px',
    padding: '0.3em',
    marginTop: '10px',
  },
  favoriteIcon: {
    color: '#2C367E;',
  },
  search: {
    height:"max-content"
  },
  indication: {
    fontSize:"0.9em"
  },
  section: {
    width: '100%',
    marginBottom: '2px',
  },
  sectionTitle: {
    color: '#2c367e',
    fontWeight: 700,
    fontSize: '0.8em',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '12px 12px 4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&::after': {
      content: '""',
      flex: 1,
      borderBottom: '2px solid #e0e0e0',
    },
  },
  sectionContent: {
    padding: '4px 0',
  },
  collectiveRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    flexWrap: 'nowrap',
  },
  collectiveLink: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '0px',
    marginRight: '4px',
    flexShrink: 0,
  },
  collectiveControl: {
    marginLeft: '0px',
    marginRight: '0px',
    width: '100%',
    '& .MuiFormControlLabel-label': {
      marginLeft: '10px',
      fontSize: '15px',
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
}));
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
   width: '100%'
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
}));

const compare = (a: { position: number }, b: { position: number }) => a.position > b.position;

const IsTree = (collection: any) => {
  let isTree = false;
  if (collection.entries) {
    collection.entries.map((entry) => {
      if (entry.subEntries) {
        entry.subEntries.map(() => {
          isTree = true;
          return isTree;
        });
      }
    });
  }
  return isTree;
};

interface FilterItemProps {
  collection: any;
  categoryChange?: any;
  onEntryChange: (entries: any[], collection: any) => void;
  initialCheckedEntries: any[];
}

const FilterItem = ({ collection, categoryChange, onEntryChange, initialCheckedEntries }: FilterItemProps) => {
  const { classes } = useStyles();

  const handleCategoryChange = useCallback((entries) => {
    onEntryChange(entries, collection);
  }, [collection, onEntryChange]);

  // Fonction récursive pour filtrer les sous-entrées
  const filterEnabledEntries = (entries) => {
    if (!entries) return null;
    return entries
      .filter(entry => entry.enabled)
      .map(entry => ({
        ...entry,
        subEntries: entry.subEntries ? filterEnabledEntries(entry.subEntries) : null
      }));
  };

  return (
    <Entries initValues={[]}>
      {
        (collection?.entries || [])
          .filter((entry: any) => entry.enabled)
          .sort(compare)
          .map((entry: any) => (
            <ParentContainer
              key={entry.id}
              entry={entry}
              onCategoryChange={handleCategoryChange}
              isForm={false}
              initialCheckedEntries={initialCheckedEntries}
            />
          ))
      }
    </Entries>
  );
};

interface FiltersProps {
  isEventList?: boolean;
  isActorList?: boolean;
  onFiltersChange?: (filters: Record<string, any>) => void;
  closeHandler?: () => void;
  inviteActor?: boolean;
  noEmailInviteActor?: boolean;
  onActiveFilterChange?: (filter: any | null) => void;
  saveDialogTrigger?: number;
}

function Filters({
  isEventList = false,
  onFiltersChange = () => {},
  closeHandler = () => {},
  inviteActor = false,
  noEmailInviteActor = false,
  onActiveFilterChange,
  saveDialogTrigger,
}: FiltersProps) {

  const GET_COLLECTIONS = gql`
  {
    collections {
        id
        label
        enabled
        multipleSelection
        position
        entries {
          id
          label
          position
          color
          icon
          description
          enabled
          subEntries {
            id
            label
            position
            color
            icon
            description
            enabled
            subEntries {
              id
              label
              position
              color
              icon
              description
              enabled
            }
          }
        }
        filter
        actor
        event
      }
    }
  `;

const GET_COLLECTIVE_ACTORS = gql`
  {
  actorsCollective {
    id
    name
    address
    shortDescription
    url
  }
}
`;




  function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
      top: `${top}%`,
      left: `${left}%`,
      height: '90%',
      overflow: 'auto',
      transform: `translate(-${top}%, -${left}%)`,
    };
  }

  const [modalStyle] = React.useState(getModalStyle);

  const [dataActorReferent, setDataActorReferent] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => (isEventList ? { periodicEvent: true } : {}));
  const [openFilters, setOpenFilters] = useState(false);
  const [openModalAddActor, setOpenModalAddActor] = useState(inviteActor);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles();
  const [favorite, setFavorite] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [savedFilterRefetch, setSavedFilterRefetch] = useState(0);
  const { showSuggestion, trackInteraction, dismiss: dismissSuggestion, acknowledged: ackSuggestion } = useNotificationSuggestion();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [activeFilter, setActiveFilter] = useState<any>(null);
  const [filterVersion, setFilterVersion] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [locationLabel, setLocationLabel] = useState('');
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [locationRadius, setLocationRadius] = useState(10);
  const [locationSuggestions, setLocationSuggestions] = useState<{ label: string; lat: number; lng: number }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const locationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const activeFilterStorageKey = useMemo(
    () => (isEventList ? 'ouaaa_active_saved_filter_events' : 'ouaaa_active_saved_filter_actors'),
    [isEventList],
  );
  const periodicEventEnabled = filters.periodicEvent !== undefined ? filters.periodicEvent : true;

  const persistActiveFilterId = useCallback((filterId) => {
    if (typeof window === 'undefined') return;
    if (filterId == null) {
      localStorage.removeItem(activeFilterStorageKey);
      return;
    }
    localStorage.setItem(activeFilterStorageKey, String(filterId));
  }, [activeFilterStorageKey]);

  const handleApplyFilter = useCallback((filterData, savedFilter) => {
    if (filterData) {
      const newFilters = { ...filterData };
      delete newFilters.postCode;
      if (isEventList && newFilters.periodicEvent === undefined) {
        newFilters.periodicEvent = true;
      }
      setFilters(newFilters);
      onFiltersChange(newFilters);
      setActiveFilter(savedFilter || null);
      setSearchValue(newFilters.search || '');
      setFavorite(!!newFilters.favoritesForUser);
      setLocationLabel(newFilters.locationLabel || '');
      setLocationLat(newFilters.locationLat ?? null);
      setLocationLng(newFilters.locationLng ?? null);
      setLocationRadius(newFilters.locationRadius ?? 10);
      persistActiveFilterId(savedFilter?.id ?? null);
      // Force re-mount of collection checkboxes
      setFilterVersion((prev) => prev + 1);
    }
  }, [isEventList, onFiltersChange, persistActiveFilterId]);

  const handleFilterSaved = useCallback((savedFilterResult) => {
    setSavedFilterRefetch((prev) => prev + 1);
    if (savedFilterResult) {
      setActiveFilter(savedFilterResult);
      persistActiveFilterId(savedFilterResult.id);
    }
  }, [persistActiveFilterId]);

  const handleClearActiveFilter = useCallback(() => {
    const clearedFilters = isEventList ? { periodicEvent: true } : {};
    setActiveFilter(null);
    setFilters(clearedFilters);
    setSearchValue('');
    setFavorite(false);
    setLocationLabel('');
    setLocationLat(null);
    setLocationLng(null);
    setLocationRadius(10);
    persistActiveFilterId(null);
    setFilterVersion((prev) => prev + 1);
    onFiltersChange(clearedFilters);
  }, [isEventList, onFiltersChange, persistActiveFilterId]);

  const applyFilterChanges = useCallback((changes) => {
    const currentFilters = {
      ...(filters || {}),
      ...changes,
    };
    setFilters(currentFilters);
    onFiltersChange(currentFilters);
    trackInteraction();
    if (activeFilter) {
      setActiveFilter((prev) => prev ? { ...prev, modified: true } : null);
    }
  }, [activeFilter, filters, onFiltersChange, trackInteraction]);

  const handleFilterChange = useCallback((name, value) => {
    applyFilterChanges({ [name]: value });
  }, [applyFilterChanges]);

  const handleDateChange = useCallback((date) => {
    handleFilterChange('startingDate', date);
  }, [handleFilterChange]);


  const handleChangeperiodicEvent = useCallback((e) => {
  
    handleFilterChange('periodicEvent', e.target.checked);
  }, [handleFilterChange]);

  // Location search via api-adresse.data.gouv.fr
  const handleLocationSearch = useCallback((value) => {
    setLocationLabel(value);
    if ((locationLat != null || locationLng != null) && value !== locationLabel) {
      setLocationLat(null);
      setLocationLng(null);
      setLocationRadius(10);
      applyFilterChanges({
        locationLabel: value || null,
        locationLat: null,
        locationLng: null,
        locationRadius: null,
      });
    }
    if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
    if (!value || value.length < 3) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    locationTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(value)}&limit=5`
        );
        const data = await res.json();
        if (data.features) {
          setLocationSuggestions(
            data.features.map((f) => ({
              label: f.properties.label,
              lat: f.geometry.coordinates[1],
              lng: f.geometry.coordinates[0],
            }))
          );
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error('Geocoding error:', err);
      }
    }, 300);
  }, [applyFilterChanges, locationLabel, locationLat, locationLng]);

  const handleSelectLocation = useCallback((suggestion) => {
    setLocationLabel(suggestion.label);
    setLocationLat(suggestion.lat);
    setLocationLng(suggestion.lng);
    setShowSuggestions(false);
    setLocationSuggestions([]);
    applyFilterChanges({
      locationLat: suggestion.lat,
      locationLng: suggestion.lng,
      locationLabel: suggestion.label,
      locationRadius,
    });
  }, [applyFilterChanges, locationRadius]);

  const handleRadiusChange = useCallback((e, newValue) => {
    setLocationRadius(newValue);
    if (locationLat != null && locationLng != null) {
      handleFilterChange('locationRadius', newValue);
    }
  }, [handleFilterChange, locationLat, locationLng]);

  const handleClearLocation = useCallback(() => {
    setLocationLabel('');
    setLocationLat(null);
    setLocationLng(null);
    setLocationRadius(10);
    setLocationSuggestions([]);
    setShowSuggestions(false);
    applyFilterChanges({
      locationLat: null,
      locationLng: null,
      locationLabel: null,
      locationRadius: null,
    });
  }, [applyFilterChanges]);

  // Notify parent of active filter changes
  useEffect(() => {
    if (onActiveFilterChange) onActiveFilterChange(activeFilter);
  }, [activeFilter, onActiveFilterChange]);

  // Allow parent to imperatively open the save dialog
  useEffect(() => {
    if (saveDialogTrigger && saveDialogTrigger > 0) {
      setShowSaveDialog(true);
    }
  }, [saveDialogTrigger]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEntryChange = useCallback((entries, collection) => {
    const currentEntries = [...(filters.entries || [])];
    entries.forEach((newEntry) => {
      const alreadyChecked = currentEntries.find((subEntries) => subEntries != undefined && subEntries.find((id) => id === newEntry.id));
      if (alreadyChecked) {
        if (!newEntry.checked) {
          currentEntries[collection.position] = currentEntries[collection.position].filter((id) => id !== newEntry.id);
        }
      } else if (newEntry.checked) {
        const collectionEntries = currentEntries[collection.position] || [];
        collectionEntries.push(newEntry.id);
        currentEntries[collection.position] = collectionEntries;
      }
    });

    handleFilterChange('entries', currentEntries);
  }, [filters, handleFilterChange]);

 
    const handleActorCollectiveChange = useCallback((entryId, checked) => {
      const currentActorsCollective = [...(filters.actorsCollective || [])];
      if (checked) {
        currentActorsCollective.push(entryId);
      } else {
        const index = currentActorsCollective.indexOf(entryId);
        if (index !== -1) {
          currentActorsCollective.splice(index, 1);
        }
      }
      handleFilterChange('actorsCollective', currentActorsCollective);
    }, [filters, handleFilterChange]);

   

  const user = useSessionState();
  const changeFavorite = (isFavorite) => {
    if (user == null) {
      enqueueSnackbar('Veuillez vous connecter pour filtrer sur vos favoris ', {
        preventDuplicate: true,
      });
    } else {
      setFavorite(isFavorite);
      if (isFavorite) {
        handleFilterChange('favoritesForUser', user.id);
      } else {
        handleFilterChange('favoritesForUser', null);
      }
    }
  };




  const { loading: loadingCollections, error: errorCollections, data: dataCollections } = useQuery(
    GET_COLLECTIONS,
    { fetchPolicy: 'network-only' },
  );
  const { loading: loadingCollectiveActors, error: errorCollectiveActors, data: dataActorCollective } = useQuery(
    GET_COLLECTIVE_ACTORS,
    { fetchPolicy: 'network-only' },
  );


  
  const handleOpenModalAddActor = () => {
    setOpenModalAddActor(true);
  };

  const handleCloseModalAddActor = () => {
    setOpenModalAddActor(false);
  };
  const filterCollections = useMemo(() => {
    if (dataCollections && dataCollections.collections && dataCollections.collections.length > 0) {
      return dataCollections.collections.filter((collection) => {
        return collection.enabled !== false && collection.filter && (isEventList ? collection.event : collection.actor);
      });
    }
    return [];
  }, [dataCollections, isEventList]);

  if (loadingCollections && !dataCollections) return 'Loading...';
  if (errorCollections) return `Error! ${errorCollections.message}`;
 

  
  const bodyModalAddActor = (
    <div style={modalStyle} className={classes.paper}>
      <IconButton
        aria-label="Close"
        className={classes.closeButton}
        onClick={() => setOpenModalAddActor(false)}
        size="large">
        <CloseIcon />
      </IconButton>
  
      <h2 id="simple-modal-title">{ noEmailInviteActor ? "Ajouter l'acteur que vous avez contacté" : 'Inviter un nouvel acteur de la transition'}</h2>
      <p className={classes.indication}>Le site vous propose d’envoyer un mail de découverte à X en votre nom. Votre mail et votre nom ne sont conservés que le temps d’envoyer le mail. Toutes les traces sont ensuite supprimées. Le destinataire reçoit un mail d’invitation lui expliquant également que ses traces (nom/adresse/mail) ne sont pas conservés et l’invitant à venir découvrir l’initiative, notamment par une prise de contact direct avant de se créer une fiche acteur. Vous pouvez contacter le Délégué de la Protection des données dpd@ouaaa-transition.fr. Pour toute question, vous pouvez nous contacter <Link href={`/contact`} target="_blank"> en cliquant ici</Link></p>
      <SuggestActorForm noEmailInviteActor={noEmailInviteActor} />
    </div>
  );
  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      wrap="nowrap"
      className={classes.root}
    >

      {/* ── Section : Recherche ── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Recherche</Typography>
        <Box className={classes.sectionContent}>
          <Search className={classes.search}>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              value={searchValue}
              onChange={(newValue) => {
                setSearchValue(newValue.currentTarget.value);
                handleFilterChange('search', newValue.currentTarget.value);
              }}
              placeholder="Recherche par nom"
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>
          <div ref={locationRef} style={{ width: '100%', position: 'relative', padding: '5px 0' }}>
            <TextField
              variant="outlined"
              label="Lieu"
              name="location"
              value={locationLabel}
              onChange={(e) => handleLocationSearch(e.target.value)}
              className={classes.postCode}
              placeholder="Rechercher une adresse..."
              InputProps={{
                startAdornment: <LocationOnIcon style={{ color: '#999', marginRight: 4 }} />,
                endAdornment: locationLabel ? (
                  <IconButton size="small" onClick={handleClearLocation}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            {locationLat == null && locationLng == null && (
              <Button
                size="small"
                fullWidth
                startIcon={<LocationOnIcon fontSize="small" />}
                onClick={() => {
                  if (typeof navigator === 'undefined' || !navigator.geolocation) {
                    enqueueSnackbar("La géolocalisation n'est pas disponible dans votre navigateur", { variant: 'warning' });
                    return;
                  }
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      const { latitude, longitude } = pos.coords;
                      setLocationLabel('Autour de moi');
                      setLocationLat(latitude as any);
                      setLocationLng(longitude as any);
                      setLocationRadius(20);
                      applyFilterChanges({
                        locationLat: latitude,
                        locationLng: longitude,
                        locationLabel: 'Autour de moi',
                        locationRadius: 20,
                      });
                    },
                    () => {
                      enqueueSnackbar("Impossible d'obtenir votre position", { variant: 'error' });
                    },
                    { enableHighAccuracy: false, timeout: 8000 },
                  );
                }}
                sx={{
                  mt: 0.5,
                  textTransform: 'none',
                  fontSize: '0.78rem',
                  color: '#69b6a9',
                  justifyContent: 'flex-start',
                }}
              >
                Suggérer 20 km autour de moi
              </Button>
            )}
            {showSuggestions && locationSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                zIndex: 1000,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: 4,
                width: '100%',
                maxHeight: 200,
                overflowY: 'auto',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}>
                {locationSuggestions.map((s, i) => (
                  <div
                    key={i}
                    onClick={() => handleSelectLocation(s)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: i < locationSuggestions.length - 1 ? '1px solid #eee' : 'none',
                      fontSize: '0.9em',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
                  >
                    {s.label}
                  </div>
                ))}
              </div>
            )}
            {locationLat != null && locationLng != null && (
              <div style={{ padding: '4px 12px 8px' }}>
                <Typography variant="caption" color="textSecondary">
                  Distance : {locationRadius} km
                </Typography>
                <Slider
                  value={locationRadius}
                  onChange={handleRadiusChange}
                  min={1}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v} km`}
                  size="small"
                />
              </div>
            )}
          </div>
        </Box>
      </Box>

      {/* ── Section : Date (événements uniquement) ── */}
      {isEventList && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Date</Typography>
          <Box className={classes.sectionContent}>
            <DateFilter onDateChange={handleDateChange} />
            <Grid container className={classes.favoriteGrid}>
              <Grid item>
                <FormControlLabel control={<Switch checked={periodicEventEnabled} onChange={handleChangeperiodicEvent} />} label="Evénements récurrents" />
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      {/* ── Section : Préférences ── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Préférences</Typography>
        <Box className={classes.sectionContent}>
          <Grid container className={classes.favoriteGrid}>
            <Grid item xs={5}>
              <div onClick={() => changeFavorite(!favorite)}>
                {favorite ? (
                  <FavoriteIcon className={classes.favoriteIcon} />
                ) : (
                  <FavoriteBorderIcon className={classes.favoriteIcon} />
                )}
              </div>
            </Grid>
            <Grid item xs={7}>
              <div>Mes favoris</div>
            </Grid>
          </Grid>

          <SavedFiltersList
            onApplyFilter={handleApplyFilter}
            refetchTrigger={savedFilterRefetch}
            activeFilterId={activeFilter?.id}
            onClearActive={handleClearActiveFilter}
            activeStorageKey={activeFilterStorageKey}
            pageFilterType={isEventList ? 'events' : 'actors'}
          />
        </Box>
      </Box>

      {/* ── Section : Catégories ── */}
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Catégories</Typography>
        <Box className={classes.sectionContent}>
          {filterCollections.map((collection) => {
            const collectionCheckedEntries = (filters.entries && filters.entries[collection.position]) || [];
            return (
              <Accordion
                key={`${collection.id}-${filterVersion}`}
                defaultExpanded={IsTree(collection)}
                className={classes.expansionPanel}
              >
                <AccordionSummary className={classes.expansionPanelSummary} expandIcon={<ExpandMoreIcon />}>
                  <Typography className={classes.collectionLabel}>
                    {collection.label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.expansionPanelDetails}>
                  <FilterItem
                    collection={collection}
                    onEntryChange={handleEntryChange}
                    initialCheckedEntries={collectionCheckedEntries}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Accordion className={classes.expansionPanel}>
            <AccordionSummary className={classes.expansionPanelSummary} expandIcon={<ExpandMoreIcon />}>
              <Typography className={classes.collectionLabel}>
                Collectif
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.expansionPanelDetails}>
              {dataActorCollective?.actorsCollective?.map((actor) => (
                <div key={actor.id} className={classes.collectiveRow}>
                  <Link href={`/acteur/${actor.url}`} className={classes.collectiveLink} target="_blank"><OpenInNewIcon /></Link>
                  <FormControlLabel
                    className={classes.collectiveControl}
                    control={<Checkbox onChange={(event) => handleActorCollectiveChange(actor.id, event.target.checked)} />}
                    label={actor.name}
                    labelPlacement="start"
                  />
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      {/* ── Actions ── */}
      <Box data-tour="save-filter">
        <SaveFilterButton
          currentFilters={filters}
          filterType={isEventList ? 'events' : 'actors'}
          onFilterSaved={handleFilterSaved}
          forceOpen={showSaveDialog}
          onDialogClose={() => setShowSaveDialog(false)}
          activeFilter={activeFilter}
        />
      </Box>

      <Box sx={{ width: '100%', textAlign: 'center', mt: 0.5, mb: 1 }}>
        <Button
          size="small"
          startIcon={<HelpOutlineIcon fontSize="small" />}
          onClick={() => setTutorialOpen(true)}
          sx={{
            textTransform: 'none',
            color: '#2c367e',
            fontSize: '0.8rem',
            '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
          }}
        >
          Comment ça marche&nbsp;?
        </Button>
      </Box>

      <FilterTutorial open={tutorialOpen} onClose={() => setTutorialOpen(false)} />

      <NotificationSuggestion
        show={showSuggestion}
        onDismiss={dismissSuggestion}
        onSaveFilter={() => {
          ackSuggestion();
          setShowSaveDialog(true);
        }}
      />

      {!isEventList && (
        <Box data-tour="invite-actor" sx={{ width: '100%', mt: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            className={classes.inviteActor}
            startIcon={<AddIcon fontSize="small" />}
            size="small"
            onClick={handleOpenModalAddActor}
          >
            Inviter un acteur non référencé
          </Button>
          {(user?.role === 'admin' || user?.role === 'acteurAdminRole') && (
            <Link href={`/admin/actors/add?proposeNewActor=true`}>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.inviteActor}
                startIcon={<AddIcon fontSize="small" />}
                size="small"
              >
                Ajouter un acteur non référencé
              </Button>
            </Link>
          )}
        </Box>
      )}

      <Modal
        open={openModalAddActor}
        onClose={handleCloseModalAddActor}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        {bodyModalAddActor}
      </Modal>
      {matches && (
        <Button
          variant="contained"
          className={classes.filterButton}
          onClick={closeHandler}
          startIcon={<DoubleArrowIcon className={classes.filterButtonIcon} />}
          color="primary"
        >
          Filtres
        </Button>
      )}
    </Grid>
  );
}

export default Filters;
